import { useEffect, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import {
  Modal,
  Input,
  PrimaryButton,
  SecondaryButton,
  Typography,
  Checkbox,
  Select,
} from '@weareopenr/component-library';
import {
  IExtension,
  IExtensionInstance,
  ICreateInstanceRequest,
  IUpdateInstanceRequest,
  IEndpointOverride,
  TSettingsValue,
} from 'src/api/extensions';
import { getAddons } from 'src/api/getAddons/getAddons';
import { useCreateInstance, useUpdateInstance } from 'src/hooks/useExtensions';
import { QueryKeys } from 'src/constants/QueryKeys';
import styles from './InstanceModal.module.scss';

interface IInstanceModalProps {
  open: boolean;
  onClose: () => void;
  brandId: string;
  extension: IExtension;
  instance?: IExtensionInstance;
}

interface IFormValues {
  name: string;
  settings: Record<string, TSettingsValue>;
  endpoints: Record<string, { url: string; is_enabled: boolean }>;
}

const normalizeName = (name: string): string => name.toLowerCase().replace(/\s+/g, '');

export const InstanceModal = ({
  open,
  onClose,
  brandId,
  extension,
  instance,
}: IInstanceModalProps) => {
  const isEdit = !!instance;
  const addonName = extension.addon.name;

  // Get existing instance names for duplicate validation (exclude current instance when editing)
  const existingNames = useMemo(
    () =>
      extension.instances
        .filter((inst) => inst.id !== instance?.id)
        .map((inst) => normalizeName(inst.name || '')),
    [extension.instances, instance?.id],
  );

  const createMutation = useCreateInstance(onClose);
  const updateMutation = useUpdateInstance(onClose);

  // Fetch addon data for endpoints and settings schema
  const { data: addons = [], isError: addonsError } = useQuery({
    queryKey: [QueryKeys.ADDONS],
    queryFn: getAddons,
    enabled: open,
  });

  // All hooks must be called before any early returns - Rules of Hooks
  const addon = useMemo(
    () => addons.find((a) => a.addon_id === extension.addon.id),
    [addons, extension.addon.id],
  );

  const addonEndpoints = addon?.endpoints || [];
  const configSchema = addon?.config_schema || [];

  // For editing, use instance endpoints; for creating, use addon endpoints
  const endpointsToShow = isEdit
    ? instance?.endpoints || []
    : addonEndpoints.map((ep) => ({
        id: ep.endpoint_id,
        type: { name: ep.name },
        url: ep.url,
        is_enabled: true,
      }));

  const defaultEndpoints = isEdit
    ? instance?.endpoints.reduce((acc, ep) => {
        acc[ep.id] = {
          url: ep.override_url || '',
          is_enabled: ep.is_enabled,
        };
        return acc;
      }, {} as Record<string, { url: string; is_enabled: boolean }>)
    : addonEndpoints.reduce((acc, ep) => {
        acc[ep.endpoint_id] = {
          url: '',
          is_enabled: true,
        };
        return acc;
      }, {} as Record<string, { url: string; is_enabled: boolean }>);

  const defaultSettings = isEdit
    ? instance?.settings || {}
    : configSchema.reduce((acc, field) => {
        acc[field.key] = field.default ?? '';
        return acc;
      }, {} as Record<string, TSettingsValue>);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: instance?.name || '',
      settings: defaultSettings || {},
      endpoints: defaultEndpoints || {},
    },
  });

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!open) {
      hasInitialized.current = false;
      return;
    }

    // Wait for addon data to load before initializing create-mode forms
    if (!isEdit && addonEndpoints.length === 0 && configSchema.length === 0) {
      return;
    }

    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const endpointDefaults = isEdit
      ? instance?.endpoints.reduce((acc, ep) => {
          acc[ep.id] = {
            url: ep.override_url || '',
            is_enabled: ep.is_enabled,
          };
          return acc;
        }, {} as Record<string, { url: string; is_enabled: boolean }>)
      : addonEndpoints.reduce((acc, ep) => {
          acc[ep.endpoint_id] = {
            url: '',
            is_enabled: true,
          };
          return acc;
        }, {} as Record<string, { url: string; is_enabled: boolean }>);

    const settingsDefaults = isEdit
      ? instance?.settings || {}
      : configSchema.reduce((acc, field) => {
          acc[field.key] = field.default ?? '';
          return acc;
        }, {} as Record<string, TSettingsValue>);

    reset({
      name: instance?.name || '',
      settings: settingsDefaults,
      endpoints: endpointDefaults || {},
    });
  }, [open, instance, reset, isEdit, addonEndpoints, configSchema]);

  // Show error if addons query fails - must be after all hooks
  if (addonsError && open) {
    return (
      <Modal closeModal={onClose} open={open}>
        <Typography size="xxlarge" weight="semi-bold" className={styles['heading']}>
          {isEdit ? 'Edit Instance' : 'New Instance'}
        </Typography>
        <Typography>Failed to load addon configuration. Please try again.</Typography>
        <div className={styles['controls']}>
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
        </div>
      </Modal>
    );
  }

  const onSubmit = (data: IFormValues) => {
    const endpointOverrides: IEndpointOverride[] = Object.entries(data.endpoints)
      .filter(([, value]) => value.url?.trim() || !value.is_enabled)
      .map(([endpointId, value]) => ({
        endpoint_id: endpointId,
        url: value.url?.trim() || null,
        is_enabled: value.is_enabled,
      }));

    const hasSettings = Object.keys(data.settings).length > 0;

    const trimmedName = data.name.trim();

    if (isEdit && instance) {
      const payload: IUpdateInstanceRequest = {
        name: trimmedName,
        settings: hasSettings ? data.settings : undefined,
        endpoints: endpointOverrides.length > 0 ? endpointOverrides : undefined,
      };
      updateMutation.mutate({
        brandId,
        extensionId: extension.id,
        instanceId: instance.id,
        payload,
      });
    } else {
      const payload: ICreateInstanceRequest = {
        name: trimmedName,
        settings: hasSettings ? data.settings : undefined,
        endpoints: endpointOverrides.length > 0 ? endpointOverrides : undefined,
      };
      createMutation.mutate({
        brandId,
        extensionId: extension.id,
        payload,
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal closeModal={onClose} open={open}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography size="xxlarge" weight="semi-bold" className={styles['heading']}>
          {isEdit ? 'Edit Instance' : 'New Instance'}
        </Typography>
        <Typography className={styles['subtitle']}>{addonName}</Typography>

        <div className={styles['form-group']}>
          <Controller
            name="name"
            control={control}
            rules={{
              required: 'Instance name is required',
              validate: (value) => {
                if (!value.trim()) {
                  return 'Instance name is required';
                }
                const normalized = normalizeName(value);
                if (existingNames.includes(normalized)) {
                  return 'An instance with this name already exists';
                }
                return true;
              },
            }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                id={field.name}
                type="text"
                label="Instance Name"
                placeholder="e.g. London Central"
                error={fieldState.error?.message}
              />
            )}
          />
        </div>

        {configSchema.length > 0 && (
          <>
            <Typography weight="semi-bold" size="medium" className={styles['section-title']}>
              Settings
            </Typography>

            {[...configSchema]
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((field) => (
                <div key={field.key} className={styles['form-group']}>
                  <Controller
                    name={`settings.${field.key}`}
                    control={control}
                    rules={{ required: field.required ? `${field.label} is required` : false }}
                    render={({ field: formField, fieldState }) => {
                      if (field.type === 'boolean') {
                        return (
                          <Checkbox
                            checked={Boolean(formField.value)}
                            onChange={(e) => formField.onChange(e.target.checked)}
                            label={field.label}
                          />
                        );
                      }
                      if (field.type === 'select' && field.options) {
                        return (
                          <Select
                            label={field.label}
                            options={field.options.map((opt) => ({ value: opt, name: opt }))}
                            value={String(formField.value || '')}
                            onChange={formField.onChange}
                          />
                        );
                      }
                      const getInputType = () => {
                        if (field.type === 'number') return 'number';
                        if (field.type === 'secret') return 'password';
                        return 'text';
                      };
                      const inputType = getInputType();
                      return (
                        <Input
                          {...formField}
                          id={formField.name}
                          value={String(formField.value || '')}
                          type={inputType}
                          label={field.label}
                          placeholder={field.placeholder}
                          error={fieldState.error?.message}
                        />
                      );
                    }}
                  />
                </div>
              ))}
          </>
        )}

        {endpointsToShow.length > 0 && (
          <>
            <Typography weight="semi-bold" size="medium" className={styles['section-title']}>
              Endpoints
            </Typography>
            <Typography className={styles['section-description']}>
              Leave URL empty to use the default endpoint.
            </Typography>

            {endpointsToShow.map((endpoint) => (
              <div key={endpoint.id} className={styles['endpoint-group']}>
                <div className={styles['endpoint-header']}>
                  <Typography weight="semi-bold">{endpoint.type.name}</Typography>
                  <Controller
                    name={`endpoints.${endpoint.id}.is_enabled`}
                    control={control}
                    defaultValue={endpoint.is_enabled}
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        label="Enabled"
                      />
                    )}
                  />
                </div>
                <Controller
                  name={`endpoints.${endpoint.id}.url`}
                  control={control}
                  defaultValue={'url' in endpoint ? endpoint.url || '' : ''}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      id={field.name}
                      type="text"
                      placeholder={endpoint.url || 'https://...'}
                      label="URL Override"
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            ))}
          </>
        )}

        <div className={styles['controls']}>
          <SecondaryButton onClick={onClose} disabled={isPending}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit" loading={isPending || isSubmitting}>
            {isEdit ? 'Save Changes' : 'Create Instance'}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
};
